import { clamp, map, times } from 'lodash';
import {
  MultiplexPeakDetectionChannelProfile,
  MultiplexPeakDetectionProfile,
  MultiplexRawDogPeakDetectionChannelProfile,
} from './types';

export const spotIllustrationWeakPeakPosition = 0.16;
export const spotIllustrationStrongPeakPosition = 0.68;

export const spotIllustrationSignalAt = (position: number) => {
  const peak = (center: number, width: number, height: number) =>
    height * Math.exp(-0.5 * ((position - center) / width) ** 2);
  return clamp(
    0.015 + peak(0.16, 0.025, 0.015) + peak(0.44, 0.11, 0.28) + peak(0.68, 0.05, 0.6) + peak(0.9, 0.025, 0.985),
    0,
    1
  );
};

export const buildSpotIllustrationSignal = (sampleCount = 100) =>
  map(times(sampleCount), (index) => {
    const position = index / (sampleCount - 1);
    return { position, value: spotIllustrationSignalAt(position) };
  });

export type CellSpotOutcome = 'eligible' | 'signal' | 'noise' | 'distance';
export const cellIllustrationDrawingAxis = 34;
export const cellIllustrationMajorAxisUm = 20;
export const cellIllustrationNucleusDiameterUm = 10;

export const cellIllustrationSpots = [
  { label: 'A', xUm: 5, yUm: 4, intensity: 0.02, background: 0.01, noiseUnits: 1 },
  { label: 'B', xUm: 3, yUm: 12, intensity: 0.035, background: 0.01, noiseUnits: 6 },
  { label: 'C', xUm: 14, yUm: 4, intensity: 0.28, background: 0.02, noiseUnits: 2.8 },
  { label: 'D', xUm: 13.5, yUm: 13.5, intensity: 0.78, background: 0.04, noiseUnits: 8 },
  { label: 'E', xUm: 14, yUm: 13.5, intensity: 0.52, background: 0.03, noiseUnits: 6 },
  { label: 'F', xUm: 10, yUm: 8, intensity: 0.15, background: 0.04, noiseUnits: 5.5 },
];

export const buildCellSpotIllustration = (
  profile: MultiplexPeakDetectionProfile,
  useRawDog: boolean,
  workingUmpp?: number | null
) => {
  const hasResolution = workingUmpp != null && Number.isFinite(workingUmpp) && workingUmpp > 0;
  const advanced = profile as MultiplexRawDogPeakDetectionChannelProfile;
  const cutoff = useRawDog ? advanced.minContrast : (profile as MultiplexPeakDetectionChannelProfile).threshold;
  const lowerBound = useRawDog ? advanced.clipMinFraction * advanced.clipMaxFraction : 0;
  const upperBound = useRawDog ? advanced.clipMaxFraction : 1;
  const prepare = (value: number) => clamp((value - lowerBound) / (upperBound - lowerBound), 0, 1);
  const spots = map(cellIllustrationSpots, (spot) => {
    const preparedPeak = useRawDog ? prepare(spot.intensity) : spot.intensity;
    const preparedBackground = useRawDog ? prepare(spot.background) : 0;
    const signal = preparedPeak - preparedBackground;
    const outcome: CellSpotOutcome =
      signal < cutoff ? 'signal' : useRawDog && spot.noiseUnits < advanced.minDogZ ? 'noise' : 'eligible';
    return {
      ...spot,
      x: (spot.xUm * cellIllustrationDrawingAxis) / cellIllustrationMajorAxisUm,
      y: (spot.yUm * cellIllustrationDrawingAxis) / cellIllustrationMajorAxisUm,
      expressionOpacity: Number((0.1 + 0.9 * spot.intensity).toFixed(3)),
      preparedPeak,
      preparedBackground,
      signal,
      outcome: outcome as CellSpotOutcome,
      distanceOutcome: outcome as CellSpotOutcome,
      distanceToStronger: Infinity,
    };
  });
  const ranked = [...spots].sort((firstSpot, secondSpot) =>
    useRawDog ? secondSpot.noiseUnits - firstSpot.noiseUnits : secondSpot.intensity - firstSpot.intensity
  );
  const accepted: typeof spots = [];
  for (const spot of ranked) {
    if (spot.outcome !== 'eligible' || !hasResolution) continue;
    spot.distanceToStronger = Math.min(
      ...map(
        accepted,
        (stronger) => Math.max(Math.abs(spot.xUm - stronger.xUm), Math.abs(spot.yUm - stronger.yUm)) / workingUmpp!
      )
    );
    if (spot.distanceToStronger < profile.minDistancePx) spot.distanceOutcome = 'distance';
    else accepted.push(spot);
  }
  return {
    spots,
    workingUmpp: hasResolution ? workingUmpp! : null,
    cellMajorAxisPixels: hasResolution ? cellIllustrationMajorAxisUm / workingUmpp! : null,
    spacingDrawingUnits: hasResolution
      ? (profile.minDistancePx * workingUmpp! * cellIllustrationDrawingAxis) / cellIllustrationMajorAxisUm
      : null,
    cutoff,
    lowerBound,
    upperBound,
    clippingFactor: useRawDog ? advanced.clipMinFraction : null,
    noiseCutoff: useRawDog ? advanced.minDogZ : null,
  };
};
